import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import type { Portal } from "@sautria/contracts";
import { useMe } from "../../shared/api/session";
import { useSignOut } from "../../shared/api/use-sign-out";
import { AppShell, Button, PageHeading } from "../../shared/ds";
import { useActiveWorkspace } from "../../shared/workspace";
import "./portal-shell.css";

export interface PortalNavItem {
  to: string;
  label: string;
  /** Strange Icons name (Asklepios) shown in the sidebar and the phone dock. */
  icon: string;
  /** Shorter label for the phone dock. */
  short?: string;
  end?: boolean;
}

/**
 * Frame shared by the portals on the DS AppShell: sidebar (rail on tablet, dock on phones), a top bar with the
 * active company, the switcher (W29) and sign out, then the page.
 */
export function PortalShell({ portal, label, nav = [], children }: { portal: Portal; label: string; nav?: PortalNavItem[]; children: ReactNode }) {
  const me = useMe();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { active, canSwitch } = useActiveWorkspace(portal);
  const isCurrent = (item: PortalNavItem) => (item.end ? pathname === item.to || pathname === `${item.to}/` : pathname.startsWith(item.to));

  return (
    <AppShell
      label={label}
      overline={label}
      brandHref={nav[0]?.to ?? "/"}
      onNavigate={(href) => navigate(href)}
      nav={nav.map((item) => ({ id: item.to, label: item.label, short: item.short, icon: item.icon, href: item.to, current: isCurrent(item) }))}
      topbar={
        <div className="portal-top">
          <span className="portal-company">
            <b>{active?.tenantName ?? label}</b>
            <span className="muted">{label}</span>
          </span>
          <div className="portal-top-actions">
            {canSwitch && (
              <Link to="/select-workspace" className="btn secondary">
                Trocar
              </Link>
            )}
            <span className="portal-user muted">{me.data?.user.email}</span>
            <Button type="button" variant="ghost" onClick={signOut}>
              Sair
            </Button>
          </div>
        </div>
      }
    >
      <div className="portal-page">{children}</div>
    </AppShell>
  );
}

/** Page title of a portal screen with its one-line lead, on the DS PageHeading. */
export function PortalHeading({ title, lead, action }: { title: string; lead?: ReactNode; action?: ReactNode }) {
  return (
    <div className="portal-heading">
      <PageHeading title={title} action={action} />
      {lead && <p className="muted">{lead}</p>}
    </div>
  );
}
